// frontend/src/Dashboard.js
import React, { useEffect } from 'react';
import './style.css';
import './RequestForm.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const navigate = useNavigate();

  useEffect(() => {
    console.log(`
      🚀 Moonlanding Dashboard Loaded!
    `);

    const updateStats = () => {
      const stats = document.querySelectorAll('.stat-number');
      stats.forEach(stat => {
        const current = parseInt(stat.textContent.replace(/[^0-9]/g, ''));
        const change = Math.floor(Math.random() * 3) + 1;
        const newValue = current + change;

        let start = current;
        const duration = 1000;
        const startTime = performance.now();

        function updateNumber(currentTime) {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);

          const value = Math.floor(start + (newValue - start) * progress);
          stat.textContent = value;

          if (progress < 1) requestAnimationFrame(updateNumber);
        }

        requestAnimationFrame(updateNumber);
      });
    };

    const interval = setInterval(updateStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRequestService = () => {
    navigate('/request-form');
  };

  // -------------------------------
  // FOOTER COMPONENT
  // -------------------------------
  const Footer = () => {
    return (
      <footer className="dashboard-footer">
        <div className="footer-content">
          <div className="footer-section">
            <div className="footer-logo">
              <i className="fas fa-rocket"></i>
              <span>Moonlanding Technologies Ltd</span>
            </div>
            <p className="footer-description">
              Transforming ideas into digital reality through innovative web, mobile, 
              and custom software solutions.
            </p>
            <div className="footer-contact">
              <div className="contact-item"><i className="fas fa-phone"></i>+254 103 140 440</div>
              <div className="contact-item"><i className="fas fa-envelope"></i>info@moonlandingtechnologies.co.ke</div>
              <div className="contact-item"><i className="fas fa-map-marker-alt"></i>Nairobi, Kenya</div>
            </div>
          </div>

          <div className="footer-section">
            <h3>Quick Links</h3>
            <ul className="footer-links">
              <li><a href="#services">Our Services</a></li>
              <li><a href="#portfolio">Portfolio</a></li>
              <li><a href="#about">About Us</a></li>
              <li><a href="#contact">Contact</a></li>
              <li><a href="#support">Support</a></li>
            </ul>
          </div>

          <div className="footer-section">
            <h3>Our Services</h3>
            <ul className="footer-links">
              <li>Web Development</li>
              <li>Mobile Apps</li>
              <li>Custom Software</li>
              <li>Consulting</li>
              <li>Cloud Solutions</li>
              <li>Payment Integration</li>
            </ul>
          </div>

          <div className="footer-section">
            <h3>Connect With Us</h3>
            <div className="social-links">
              <a href="https://facebook.com/moonlanding" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-facebook"></i>
              </a>
              <a href="https://twitter.com/moonlanding" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-twitter"></i>
              </a>
              <a href="https://linkedin.com/company/moonlanding" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-linkedin"></i>
              </a>
              <a href="https://instagram.com/moonlanding" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-instagram"></i>
              </a>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} Moonlanding Technologies Ltd. All rights reserved.</p>
        </div>
      </footer>
    );
  };

  // ---------------------------------------------------
  // MAIN JSX RETURN
  // ---------------------------------------------------
  return (
    <div className="dashboard-container">

      {/* HEADER */}
      <header className="header">
        <div className="header-left">
          <div className="logo">
            <i className="fas fa-rocket"></i>
            <span>Moonlanding Technologies</span>
          </div>
        </div>
        <div className="header-right">
          <button className="request-service-btn" onClick={handleRequestService}>
            <i className="fas fa-plus"></i>
            Request Service
          </button>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main className="main-content">

        {/* Welcome */}
        <section className="welcome-section">
          <h1>Welcome to Your Dashboard</h1>
          <p>Manage and monitor all your software products in one place</p>
        </section>

        {/* Stats */}
        <div className="stats-grid">
          {[
            ['fas fa-globe', 'Websites', '24', '+5 this month'],
            ['fas fa-mobile-alt', 'Mobile Apps', '18', '+3 this month'],
            ['fas fa-laptop-code', 'Software Products', '12', '+2 this month'],
            ['fas fa-chart-line', 'Revenue', '$156K', '+12% growth'],
          ].map(([icon, title, number, change], i) => (
            <div className="stat-card" key={i}>
              <div className="stat-icon"><i className={icon}></i></div>
              <div className="stat-info">
                <h3>{title}</h3>
                <span className="stat-number">{number}</span>
                <span className="stat-change">{change}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <section className="quick-actions">
          <h2>Quick Actions</h2>
          <div className="actions-grid">
            <div className="action-card" onClick={handleRequestService}>
              <div className="action-icon"><i className="fas fa-code"></i></div>
              <h3>Request New Service</h3>
              <p>Start a new website, mobile app, or software project</p>
              <button className="action-btn">Get Started</button>
            </div>

            <div className="action-card">
              <div className="action-icon"><i className="fas fa-project-diagram"></i></div>
              <h3>View Projects</h3>
              <p>Check the status of your ongoing projects</p>
              <button className="action-btn">View All</button>
            </div>

            <div className="action-card">
              <div className="action-icon"><i className="fas fa-file-invoice-dollar"></i></div>
              <h3>Billing & Payments</h3>
              <p>Manage your invoices and payment methods</p>
              <button className="action-btn">Manage</button>
            </div>
          </div>
        </section>

        {/* Products */}
        <section className="products-section">
          <h2>Our Products</h2>
          <div className="products-grid">
            {[
              ['website', 'fas fa-desktop', 'Web Development', 'Custom websites...', '24 Active Projects', '98% Satisfaction'],
              ['mobile', 'fas fa-mobile-alt', 'Mobile Apps', 'iOS and Android...', '18 Live Apps', '4.8★ Rating'],
              ['software', 'fas fa-cogs', 'Software Solutions', 'Enterprise software...', '12 Products', '100% ROI']
            ].map(([type, icon, title, desc, stat1, stat2], i) => (
              <div className="product-card" key={i}>
                <div className={`product-icon ${type}`}><i className={icon}></i></div>
                <h3>{title}</h3>
                <p>{desc}</p>
                <div className="product-stats">
                  <span>{stat1}</span>
                  <span>{stat2}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Recent Activity */}
        <section className="activity-section">
          <h2>Recent Activity</h2>
          <div className="activity-list">
            <div className="activity-item">
              <div className="activity-icon success"><i className="fas fa-check-circle"></i></div>
              <div className="activity-content">
                <p>New website launched for mybusiness.co.ke</p>
                <span>2 hours ago</span>
              </div>
            </div>

            <div className="activity-item">
              <div className="activity-icon warning"><i className="fas fa-exclamation-triangle"></i></div>
              <div className="activity-content">
                <p>Mobile app update in review</p>
                <span>5 hours ago</span>
              </div>
            </div>

            <div className="activity-item">
              <div className="activity-icon info"><i className="fas fa-info-circle"></i></div>
              <div className="activity-content">
                <p>New feature deployed to production</p>
                <span>1 day ago</span>
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* FOOTER */}
      <Footer />
    </div>
  );
};

export default Dashboard;
